import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, useMap, useMapEvents } from "react-leaflet";
import { ArrowLeft, Plus, Trash, MapPin, PencilSimple, MagnifyingGlass, X, Phone } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, formatApiError, adminLogout } from "@/lib/api";
import { pinIcon } from "@/lib/map";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const DEFAULT_CENTER = [-6.2088, 106.8456];

function ClickHandler({ onPick }) {
  useMapEvents({
    click(e) {
      onPick({ lat: e.latlng.lat, lng: e.latlng.lng });
    },
  });
  return null;
}

function FlyTo({ position }) {
  const map = useMap();
  useEffect(() => {
    if (position) map.flyTo(position, 16, { duration: 1 });
  }, [position, map]);
  return null;
}

function isGoogleMapsLink(text) {
  return /(google\.[a-z.]+\/maps|maps\.app\.goo\.gl|goo\.gl\/maps)/.test(text);
}

const emptyForm = { name: "", address: "", contact_name: "", contact_phone: "", gmaps_url: "", lat: null, lng: null };

export default function ShowroomManager() {
  const navigate = useNavigate();
  const [showrooms, setShowrooms] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [focus, setFocus] = useState(null);
  const markerRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/showrooms");
      setShowrooms(data);
    } catch (err) {
      if (err?.response?.status === 401) {
        adminLogout();
        navigate("/");
      } else {
        toast.error(formatApiError(err));
      }
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resolveLinkToPin = async (url) => {
    try {
      const { data } = await api.post("/admin/resolve-gmaps", { url });
      setForm((f) => ({ ...f, lat: data.lat, lng: data.lng }));
      setFocus([data.lat, data.lng]);
      toast.success("Pinpoint set from this link");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const pickLocation = ({ lat, lng, label, address, gmapsUrl }) => {
    setForm((f) => ({
      ...f,
      lat,
      lng,
      address: f.address || address || "",
      gmaps_url: gmapsUrl || f.gmaps_url,
    }));
    setFocus([lat, lng]);
    setResults([]);
    setQuery("");
    toast.success(label ? `Pin set: ${label}` : "Pin set");
  };

  const searchPlace = async (e) => {
    e.preventDefault();
    const text = query.trim();
    if (!text) return;
    if (isGoogleMapsLink(text)) {
      setSearching(true);
      try {
        const { data } = await api.post("/admin/resolve-gmaps", { url: text });
        pickLocation({ ...data, label: "Exact location from Google Maps", gmapsUrl: text });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setSearching(false);
      }
      return;
    }
    setSearching(true);
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&limit=5&q=${encodeURIComponent(text)}`
      );
      const data = await res.json();
      setResults(data);
      if (data.length === 0) toast.error("No places found. Try a more specific name or paste a Google Maps link.");
    } catch {
      toast.error("Search failed. Check your connection.");
    } finally {
      setSearching(false);
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
    setQuery("");
    setResults([]);
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("Showroom name is required");
    if (form.lat == null || form.lng == null)
      return toast.error("Set the showroom location first (search, paste a link, or click the map)");
    setSaving(true);
    const payload = {
      name: form.name.trim(),
      address: form.address,
      contact_name: form.contact_name,
      contact_phone: form.contact_phone,
      gmaps_url: form.gmaps_url,
      lat: form.lat,
      lng: form.lng,
    };
    try {
      if (editingId) {
        await api.put(`/admin/showrooms/${editingId}`, payload);
        toast.success("Showroom updated");
      } else {
        await api.post("/admin/showrooms", payload);
        toast.success("Showroom added to database");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const editShowroom = (s) => {
    setEditingId(s.id);
    setForm({
      name: s.name,
      address: s.address || "",
      contact_name: s.contact_name || "",
      contact_phone: s.contact_phone || "",
      gmaps_url: s.gmaps_url || "",
      lat: s.lat,
      lng: s.lng,
    });
    setFocus([s.lat, s.lng]);
  };

  const removeShowroom = async (s) => {
    if (!window.confirm(`Delete "${s.name}" from the database? It will also be removed from every client tour.`)) return;
    try {
      await api.delete(`/admin/showrooms/${s.id}`);
      if (editingId === s.id) resetForm();
      toast.success("Showroom deleted");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const center = useMemo(() => {
    if (showrooms?.length) return [showrooms[0].lat, showrooms[0].lng];
    return DEFAULT_CENTER;
  }, [showrooms]);

  return (
    <div className="min-h-screen bg-background" data-testid="showroom-manager">
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-8 py-5">
          <div className="flex items-center gap-4">
            <Link to="/admin" data-testid="back-to-dashboard">
              <Button variant="ghost" size="sm" className="rounded-full">
                <ArrowLeft className="mr-1 h-4 w-4" /> Clients
              </Button>
            </Link>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                Master Database
              </p>
              <h1 className="font-serif text-3xl font-light tracking-tighter">Showrooms</h1>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto grid max-w-7xl grid-cols-1 gap-8 px-8 py-10 md:grid-cols-12">
        <div className="space-y-8 md:col-span-5">
          <form onSubmit={save} className="rounded-2xl border border-border bg-card p-8" data-testid="master-showroom-form">
            <div className="flex items-center justify-between">
              <h2 className="font-serif text-2xl font-light tracking-tight">
                {editingId ? "Edit Showroom" : "Add Showroom"}
              </h2>
              {editingId && (
                <Button type="button" variant="ghost" size="sm" onClick={resetForm} className="rounded-full" data-testid="cancel-edit-button">
                  <X className="mr-1 h-4 w-4" /> Cancel
                </Button>
              )}
            </div>

            <div className="mt-6 space-y-5">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Name
                </label>
                <Input
                  data-testid="showroom-name-input"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  placeholder="e.g. Maison Living Senopati"
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Full address
                </label>
                <Input
                  data-testid="showroom-address-input"
                  value={form.address}
                  onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))}
                  placeholder="Street, district, city"
                  className="mt-2 h-11 rounded-xl"
                />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Location pinpoint
                </label>
                <div className="relative mt-2">
                  <div className="flex gap-2">
                    <Input
                      data-testid="location-search-input"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") searchPlace(e);
                      }}
                      placeholder="Search a place, or paste a Google Maps link…"
                      className="h-11 rounded-xl"
                    />
                    <Button
                      data-testid="location-search-button"
                      type="button"
                      variant="outline"
                      onClick={searchPlace}
                      disabled={searching}
                      className="h-11 rounded-xl px-4"
                    >
                      <MagnifyingGlass className="h-4 w-4" />
                    </Button>
                  </div>
                  {results.length > 0 && (
                    <div className="absolute z-20 mt-2 w-full overflow-hidden rounded-xl border border-border bg-card shadow-lg" data-testid="location-search-results">
                      {results.map((r, i) => (
                        <button
                          key={r.place_id || i}
                          type="button"
                          data-testid={`location-result-${i}`}
                          onClick={() =>
                            pickLocation({
                              lat: parseFloat(r.lat),
                              lng: parseFloat(r.lon),
                              label: r.display_name?.split(",")[0],
                              address: r.display_name,
                            })
                          }
                          className="block w-full border-b border-border px-4 py-3 text-left text-xs leading-relaxed transition-colors duration-150 last:border-0 hover:bg-secondary/60"
                        >
                          {r.display_name}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
                <p className="mt-2 rounded-xl border border-dashed border-border bg-secondary/50 px-4 py-3 text-sm text-muted-foreground" data-testid="showroom-coords-display">
                  {form.lat != null
                    ? `${form.lat.toFixed(5)}, ${form.lng.toFixed(5)} — click the map or drag the pin to adjust`
                    : "Search above, paste a Google Maps link, or click on the map to place the pin"}
                </p>
              </div>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    Contact person
                  </label>
                  <Input
                    data-testid="contact-name-input"
                    value={form.contact_name}
                    onChange={(e) => setForm((f) => ({ ...f, contact_name: e.target.value }))}
                    placeholder="e.g. Ms. Ratna"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
                <div>
                  <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                    WhatsApp number
                  </label>
                  <Input
                    data-testid="contact-phone-input"
                    value={form.contact_phone}
                    onChange={(e) => setForm((f) => ({ ...f, contact_phone: e.target.value }))}
                    placeholder="e.g. 0812 3456 7890"
                    className="mt-2 h-11 rounded-xl"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                  Google Maps link
                </label>
                <Input
                  data-testid="gmaps-url-input"
                  value={form.gmaps_url}
                  onChange={(e) => setForm((f) => ({ ...f, gmaps_url: e.target.value }))}
                  onBlur={(e) => {
                    const v = e.target.value.trim();
                    if (v && isGoogleMapsLink(v)) resolveLinkToPin(v);
                  }}
                  placeholder="https://maps.app.goo.gl/…"
                  className="mt-2 h-11 rounded-xl"
                />
                <p className="mt-1.5 text-xs text-muted-foreground">
                  Shown to clients as the "Open in Google Maps" button — pasting a link here also sets the pinpoint. If empty, the pin coordinates are used instead.
                </p>
              </div>
              <Button
                data-testid="save-showroom-button"
                type="submit"
                disabled={saving}
                className="h-11 w-full rounded-full bg-primary text-primary-foreground transition-transform duration-200 hover:scale-[1.01] hover:bg-primary"
              >
                {saving ? "Saving…" : editingId ? "Update Showroom" : "Add to Database"}
              </Button>
            </div>
          </form>

          <div className="space-y-3" data-testid="showroom-list">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Database · {showrooms?.length ?? 0} showroom{showrooms?.length === 1 ? "" : "s"}
            </p>
            {showrooms === null ? (
              <p className="text-sm text-muted-foreground">Loading…</p>
            ) : showrooms.length === 0 ? (
              <p className="rounded-2xl border border-dashed border-border bg-card p-8 text-sm text-muted-foreground">
                No showrooms yet. Add your first one above — it becomes selectable in every client tour.
              </p>
            ) : (
              showrooms.map((s) => (
                <div key={s.id} data-testid={`showroom-row-${s.id}`} className="flex items-center justify-between rounded-2xl border border-border bg-card p-5">
                  <div className="min-w-0">
                    <p className="truncate font-serif text-xl font-light">{s.name}</p>
                    <p className="truncate text-xs text-muted-foreground">{s.address || "No address"}</p>
                    {s.contact_name && (
                      <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                        <Phone className="h-3 w-3" /> {s.contact_name}
                        {s.contact_phone ? ` · ${s.contact_phone}` : ""}
                      </p>
                    )}
                  </div>
                  <div className="ml-4 flex gap-1">
                    <Button data-testid={`edit-showroom-${s.id}`} variant="ghost" size="sm" onClick={() => editShowroom(s)} className="rounded-full">
                      <PencilSimple className="h-4 w-4" />
                    </Button>
                    <Button data-testid={`delete-showroom-${s.id}`} variant="ghost" size="sm" onClick={() => removeShowroom(s)} className="rounded-full text-muted-foreground hover:text-destructive">
                      <Trash className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        <div className="md:col-span-7">
          <div className="sticky top-8 overflow-hidden rounded-2xl border border-border">
            <MapContainer center={center} zoom={12} className="h-[70vh] w-full" data-testid="admin-map">
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ClickHandler onPick={({ lat, lng }) => setForm((f) => ({ ...f, lat, lng }))} />
              <FlyTo position={focus} />
              {(showrooms || []).map((s, i) => (
                <Marker
                  key={s.id}
                  position={[s.lat, s.lng]}
                  icon={pinIcon(i + 1, { active: editingId === s.id })}
                  eventHandlers={{ click: () => editShowroom(s) }}
                />
              ))}
              {form.lat != null && (
                <Marker
                  position={[form.lat, form.lng]}
                  icon={pinIcon(editingId ? undefined : "+", { draft: !editingId, active: Boolean(editingId) })}
                  draggable
                  ref={markerRef}
                  eventHandlers={{
                    dragend: () => {
                      const m = markerRef.current;
                      if (m) {
                        const { lat, lng } = m.getLatLng();
                        setForm((f) => ({ ...f, lat, lng }));
                      }
                    },
                  }}
                />
              )}
            </MapContainer>
            <div className="flex items-center gap-2 border-t border-border bg-card px-5 py-3">
              <MapPin className="h-4 w-4 text-accent" />
              <p className="text-xs text-muted-foreground">
                Search a place, paste a Google Maps link, or click the map — then drag the pin to fine-tune.
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
